import {
  Modal,
  ModalOverlay,
  Dialog as ReactAriaDialog,
} from "react-aria-components";
import { Text } from "../Text/Text";
import styles from "./Dialog.module.scss";
import { Heading } from "../Heading/Heading";
import { useId, type CSSProperties } from "react";
import { useLingui } from "@lingui/react/macro";
import { FilledButton, OutlinedButton } from "../Button/Button";

type BaseDialogProps = {
  title?: string;
  message: string;
  role: "dialog" | "alertdialog";
  isOpen: boolean;
  actions: (close: () => void) => React.ReactNode;
  onClose?: () => void;
};

/**
 * Base Dialog component for the unified dialog system.
 * This component provides the foundation for AlertDialog and ConfirmDialog.
 * Use the useDialog hook to access alert() and confirm() functions.
 */
const BaseDialog = ({
  title,
  message,
  role,
  isOpen,
  actions,
  onClose,
}: BaseDialogProps) => {
  const dialogId = useId();
  const headingId = useId();
  const messageId = useId();

  const textAsArray = message?.split("\n");
  const textAsArrayWithoutEmptyLines = textAsArray?.filter(
    (line) => line.trim() !== "",
  );
  const hasTitle = title && title.trim() !== "";
  const hasMessage =
    message &&
    textAsArrayWithoutEmptyLines &&
    textAsArrayWithoutEmptyLines.length > 0;

  const close = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open && onClose) {
          onClose();
        }
      }}
      className={styles.overlay}
    >
      <Modal>
        <ReactAriaDialog
          role={role}
          className={styles.dialog}
          id={dialogId}
          aria-labelledby={
            hasTitle ? headingId : hasMessage ? messageId : undefined
          }
          aria-describedby={hasTitle && hasMessage ? messageId : undefined}
        >
          <>
            {hasTitle ? (
              <Heading id={headingId} level="h1" size="h2">
                {title}
              </Heading>
            ) : null}
            {hasMessage ? (
              <div id={messageId}>
                {textAsArrayWithoutEmptyLines?.map((line, idx) => (
                  <Text key={idx} element="p">
                    {line}
                  </Text>
                ))}
              </div>
            ) : null}
            <div className={styles.actions}>{actions(close)}</div>
          </>
        </ReactAriaDialog>
      </Modal>
    </ModalOverlay>
  );
};

type CustomDialogProps = {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  /** Width in px, 500 by default. Narrow viewports still shrink the dialog. */
  preferredWidth?: 500 | 720 | 960;
  /** Buttons laid out like ConfirmDialog's, after the content. */
  actions?: React.ReactNode;
  children: React.ReactNode;
};

/**
 * A dialog with a title, any content and optional actions, e.g. a form whose
 * submit button is linked to it with the `form` attribute.
 *
 * Escape hatch: only use this when no other dialog variant fits. For a plain
 * alert or confirmation, use useDialog. If the same kind of dialog is
 * expected to be used in more places, build a dedicated variant for it
 * instead.
 */
const CustomDialog = ({
  title,
  isOpen,
  onClose,
  preferredWidth = 500,
  actions,
  children,
}: CustomDialogProps) => {
  const headingId = useId();
  const preferredWidthStyle: CSSProperties & { "--preferred-width"?: string } =
    {
      "--preferred-width": `${preferredWidth}px`,
    };

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      className={styles.overlay}
    >
      <Modal>
        <ReactAriaDialog
          className={styles.dialog}
          style={preferredWidthStyle}
          aria-labelledby={headingId}
        >
          <Heading id={headingId} level="h1" size="h2">
            {title}
          </Heading>
          {children}
          {actions ? <div className={styles.actions}>{actions}</div> : null}
        </ReactAriaDialog>
      </Modal>
    </ModalOverlay>
  );
};

type AlertDialogProps = StrictOmit<BaseDialogProps, "actions" | "role"> & {
  onConfirm: () => void;
};

const AlertDialog = ({
  title,
  message,
  isOpen,
  onConfirm,
  onClose,
}: AlertDialogProps) => {
  const { t } = useLingui();

  return (
    <BaseDialog
      title={title}
      message={message}
      role="alertdialog"
      isOpen={isOpen}
      onClose={onClose}
      actions={(close) => (
        <FilledButton
          onClick={() => {
            onConfirm();
            close();
          }}
        >
          {t`OK`}
        </FilledButton>
      )}
    />
  );
};

type ConfirmDialogProps = StrictOmit<
  BaseDialogProps,
  "actions" | "role" | "title"
> & {
  title: string;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
};

const ConfirmDialog = ({
  title,
  message,
  confirmLabel,
  isOpen,
  onConfirm,
  onCancel,
  onClose,
}: ConfirmDialogProps) => {
  const { t } = useLingui();

  return (
    <BaseDialog
      title={title}
      message={message}
      role="alertdialog"
      isOpen={isOpen}
      onClose={onClose}
      actions={(close) => (
        <>
          <OutlinedButton
            onClick={() => {
              onCancel();
              close();
            }}
          >
            {t`Avbryt`}
          </OutlinedButton>
          <FilledButton
            onClick={() => {
              onConfirm();
              close();
            }}
          >
            {confirmLabel ?? t`OK`}
          </FilledButton>
        </>
      )}
    />
  );
};

export { AlertDialog, ConfirmDialog, CustomDialog };
